/* Application state — the store's interface, with API persistence.
 * Counterparty timers are gone. A failed read does not fall back to seed. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ToastTone } from '@nextdone/ui';
import type { Deal, Job, Me, MilestoneSpec } from '@nextdone/shared';
import { api, ApiError, setSessionToken, setUnauthorizedHandler } from '../lib/api';
import { clearStoredToken, readStoredToken, writeStoredToken } from '../lib/session';
import { isShotHarness } from '../lib/shot';
import { clearDevInitData, readTelegramInitData } from '../lib/telegram';
import { buildSeed } from './seed';
import type { LoadStatus, State, ToastMsg } from './types';
import { party, tierFor } from './types';

/* ---------- actions ---------- */

type Action =
  | { type: 'HYDRATE'; me: Me | null; jobs: Job[]; deals: Deal[] }
  | { type: 'UPSERT_JOB'; job: Job }
  | { type: 'UPSERT_DEAL'; deal: Deal }
  | { type: 'TOAST_PUSH'; toast: ToastMsg }
  | { type: 'TOAST_DISMISS'; id: number };

function reduce(state: State, a: Action): State {
  switch (a.type) {
    case 'HYDRATE':
      return { ...state, me: a.me, jobs: a.jobs, deals: a.deals };
    case 'UPSERT_JOB':
      return {
        ...state,
        jobs: state.jobs.some((j) => j.id === a.job.id)
          ? state.jobs.map((j) => (j.id === a.job.id ? a.job : j))
          : [a.job, ...state.jobs],
      };
    case 'UPSERT_DEAL':
      return {
        ...state,
        deals: state.deals.some((d) => d.id === a.deal.id)
          ? state.deals.map((d) => (d.id === a.deal.id ? a.deal : d))
          : [a.deal, ...state.deals],
      };
    case 'TOAST_PUSH':
      return { ...state, toasts: [...state.toasts.slice(-2), a.toast] };
    case 'TOAST_DISMISS':
      return { ...state, toasts: state.toasts.filter((t) => t.id !== a.id) };
  }
}

function emptyState(): State {
  return { me: null, jobs: [], deals: [], toasts: [] };
}

function initialState(): State {
  if (!isShotHarness()) return emptyState();
  const injected = window.__ND_SHOT_STATE__;
  if (injected) return { ...injected, toasts: [] };
  return buildSeed();
}

/* ---------- session ---------- */

class UnauthenticatedError extends Error {
  constructor() {
    super('No Telegram session');
    this.name = 'UnauthenticatedError';
  }
}

let authInflight: Promise<string> | null = null;

async function ensureSession(): Promise<string> {
  const existing = readStoredToken();
  if (existing) {
    setSessionToken(existing);
    return existing;
  }
  if (authInflight) return authInflight;
  authInflight = (async () => {
    const initData = readTelegramInitData();
    if (!initData) throw new UnauthenticatedError();
    try {
      const { token } = await api.auth(initData);
      writeStoredToken(token);
      setSessionToken(token);
      clearDevInitData();
      return token;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) clearDevInitData();
      throw err;
    }
  })().finally(() => {
    authInflight = null;
  });
  return authInflight;
}

function failMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/* ---------- context ---------- */

export interface StoreApi {
  state: State;
  status: LoadStatus;
  error: string | null;
  reload: () => Promise<void>;
  postJob: (input: {
    title: string;
    category: Job['category'];
    description: string;
    milestones: MilestoneSpec[];
    autoRelease: boolean;
  }) => Promise<Job>;
  submitProposal: (jobId: string, input: { price: number; days: number; message: string }) => Promise<void>;
  acceptProposal: (jobId: string, proposalId: string) => Promise<Deal | undefined>;
  declineProposal: (jobId: string, proposalId: string) => Promise<void>;
  deliverMilestone: (dealId: string, index: number, note: string) => Promise<void>;
  releaseMilestone: (dealId: string, index: number) => Promise<void>;
  openDispute: (dealId: string, reason: string) => Promise<void>;
  withdrawDispute: (dealId: string) => Promise<void>;
  rateDeal: (dealId: string, rating: number) => Promise<void>;
  syncDeal: (dealId: string) => Promise<void>;
  ensureJob: (id: string) => Promise<Job | undefined>;
  ensureDeal: (id: string) => Promise<Deal | undefined>;
  toast: (tone: ToastTone, text: string, detail?: string, to?: string) => void;
  dismissToast: (id: number) => void;
}

const Ctx = createContext<StoreApi | null>(null);

let toastSerial = 1;

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reduce, undefined, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const [status, setStatus] = useState<LoadStatus>(isShotHarness() ? 'ready' : 'loading');
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  const toast = useCallback((tone: ToastTone, text: string, detail?: string, to?: string) => {
    const id = toastSerial++;
    dispatch({ type: 'TOAST_PUSH', toast: { id, tone, text, detail, to } });
    setTimeout(() => dispatch({ type: 'TOAST_DISMISS', id }), 5200);
  }, []);

  const hydrateFromApi = useCallback(async () => {
    const [me, jobs, deals] = await Promise.all([api.profile(), api.jobs(), api.deals()]);
    dispatch({ type: 'HYDRATE', me, jobs, deals });
  }, []);

  const reload = useCallback(async () => {
    if (isShotHarness()) {
      setStatus('ready');
      setError(null);
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      await ensureSession();
      await hydrateFromApi();
      setStatus('ready');
    } catch (err) {
      if (err instanceof UnauthenticatedError || (err instanceof ApiError && err.status === 401)) {
        setSessionToken(null);
        clearStoredToken();
        dispatch({ type: 'HYDRATE', me: null, jobs: [], deals: [] });
        setStatus('unauthenticated');
        setError(null);
        return;
      }
      setStatus('error');
      setError(failMessage(err, 'The ledger did not respond.'));
    }
  }, [hydrateFromApi]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSessionToken(null);
      clearStoredToken();
      dispatch({ type: 'HYDRATE', me: null, jobs: [], deals: [] });
      if (statusRef.current !== 'unauthenticated') {
        setStatus('unauthenticated');
        setError(null);
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    if (isShotHarness()) {
      window.__ND_LEDGER__ = {
        job: stateRef.current.jobs[0]?.id ?? null,
        deal: stateRef.current.deals[0]?.id ?? null,
      };
      return;
    }
    void reload();
  }, [reload]);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>, fail: string): Promise<T> => {
      try {
        return await fn();
      } catch (err) {
        toast('neutral', fail, failMessage(err, fail));
        throw err;
      }
    },
    [toast],
  );

  const apiSurface = useMemo<StoreApi>(() => {
    const shot = isShotHarness();

    return {
      state,
      status,
      error,
      reload,

      async postJob(input) {
        if (shot) {
          const me = stateRef.current.me;
          const job: Job = {
            id: `s${stateRef.current.jobs.length + 1}`,
            title: input.title,
            category: input.category,
            description: input.description,
            budget: input.milestones.reduce((s, m) => s + m.amount, 0),
            milestones: input.milestones,
            client: party(me?.name ?? 'You', tierFor(me?.staked ?? 0)),
            mine: true,
            postedAt: 0,
            status: 'open',
            proposals: [],
            proposalCount: 0,
            autoRelease: input.autoRelease,
            depositTx: null,
          };
          dispatch({ type: 'UPSERT_JOB', job });
          return job;
        }
        return run(async () => {
          const job = await api.createJob(input);
          dispatch({ type: 'UPSERT_JOB', job });
          return job;
        }, 'Post failed');
      },

      async submitProposal(jobId, input) {
        if (shot) return;
        await run(async () => {
          await api.createProposal(jobId, input);
          const job = await api.job(jobId);
          dispatch({ type: 'UPSERT_JOB', job });
        }, 'Proposal failed');
        toast('neutral', 'Proposal sent', `JOB ${jobId} / ${input.price} TON / ${input.days} DAYS`);
      },

      async acceptProposal(jobId, proposalId) {
        if (shot) return undefined;
        return run(async () => {
          const deal = await api.acceptProposal(jobId, proposalId);
          dispatch({ type: 'UPSERT_DEAL', deal });
          const job = await api.job(jobId);
          dispatch({ type: 'UPSERT_JOB', job });
          toast(
            'neutral',
            'Hired',
            deal.contractAddress
              ? `DEAL ${deal.id} / ${deal.contractAddress}`
              : `DEAL ${deal.id} / NO CONTRACT ATTACHED`,
            `/deals/${deal.id}`,
          );
          return deal;
        }, 'Hire failed');
      },

      async declineProposal(jobId, proposalId) {
        if (shot) return;
        await run(async () => {
          await api.declineProposal(jobId, proposalId);
          const job = await api.job(jobId);
          dispatch({ type: 'UPSERT_JOB', job });
        }, 'Decline failed');
      },

      async deliverMilestone(dealId, index, note) {
        if (shot) return;
        await run(async () => {
          const deal = await api.deliver(dealId, index, note);
          dispatch({ type: 'UPSERT_DEAL', deal });
        }, 'Delivery note failed');
        toast('neutral', `M${index + 1} note recorded`, 'STATUS FOLLOWS CHAIN, NOT THIS REQUEST');
      },

      async releaseMilestone(dealId, index) {
        if (shot) return;
        await run(async () => {
          const deal = await api.release(dealId, index);
          dispatch({ type: 'UPSERT_DEAL', deal });
        }, 'Release is a wallet message — the API only indexes');
      },

      async openDispute(dealId, reason) {
        if (shot) return;
        await run(async () => {
          const deal = await api.dispute(dealId, reason);
          dispatch({ type: 'UPSERT_DEAL', deal });
        }, 'Dispute failed');
        toast('dispute', 'Dispute opened', `DEAL ${dealId} / CLOCK FROZEN`);
      },

      async withdrawDispute(dealId) {
        if (shot) return;
        await run(async () => {
          const deal = await api.withdrawDispute(dealId);
          dispatch({ type: 'UPSERT_DEAL', deal });
        }, 'Withdraw failed');
        toast('neutral', 'Dispute withdrawn', `DEAL ${dealId}`);
      },

      async rateDeal(dealId, rating) {
        if (shot) return;
        await run(async () => {
          const deal = await api.rate(dealId, rating);
          dispatch({ type: 'UPSERT_DEAL', deal });
        }, 'Rating failed');
      },

      async syncDeal(dealId) {
        if (shot) return;
        await run(async () => {
          const deal = await api.sync(dealId);
          dispatch({ type: 'UPSERT_DEAL', deal });
        }, 'Chain sync failed');
      },

      async ensureJob(id) {
        const found = stateRef.current.jobs.find((j) => j.id === id);
        if (found) return found;
        if (shot) return undefined;
        try {
          const job = await api.job(id);
          dispatch({ type: 'UPSERT_JOB', job });
          return job;
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) return undefined;
          throw err;
        }
      },

      async ensureDeal(id) {
        const found = stateRef.current.deals.find((d) => d.id === id);
        if (found) return found;
        if (shot) return undefined;
        try {
          const deal = await api.deal(id);
          dispatch({ type: 'UPSERT_DEAL', deal });
          return deal;
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) return undefined;
          throw err;
        }
      },

      toast,
      dismissToast(id) {
        dispatch({ type: 'TOAST_DISMISS', id });
      },
    };
  }, [state, status, error, reload, run, toast]);

  return <Ctx.Provider value={apiSurface}>{children}</Ctx.Provider>;
}

export function useStore(): StoreApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStore outside <StoreProvider>');
  return ctx;
}

export function useEnsured<T extends { id: string }>(
  id: string | undefined,
  fromState: T | undefined,
  ensure: (id: string) => Promise<T | undefined>,
  ready: boolean,
): { item: T | undefined; load: 'idle' | 'loading' | 'gone' | 'error'; error: string | null } {
  const [load, setLoad] = useState<'idle' | 'loading' | 'gone' | 'error'>(
    fromState || !ready ? 'idle' : 'loading',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !ready || fromState) {
      if (fromState) {
        setLoad('idle');
        setError(null);
      }
      return;
    }
    let live = true;
    setLoad('loading');
    void ensure(id)
      .then((item) => {
        if (!live) return;
        setLoad(item ? 'idle' : 'gone');
      })
      .catch((err: unknown) => {
        if (!live) return;
        setLoad('error');
        setError(failMessage(err, 'Unread'));
      });
    return () => {
      live = false;
    };
  }, [id, fromState, ensure, ready]);

  const effective =
    fromState || !ready ? 'idle' : load === 'idle' ? 'loading' : load;
  return { item: fromState, load: effective, error };
}
